import { useEffect, type ReactElement } from "react";
import { Camera, X } from "lucide-react";
import { Button, IconButton, Stack, Text } from "@ux/primitives";
import { palette, space } from "@ux/tokens";
import {
    useBarcodeScanner,
    type BarcodeScanResult,
    type UseBarcodeScannerOptions,
} from "./useBarcodeScanner";

export interface BarcodeScannerSheetProps {
    readonly open: boolean;
    readonly title?: string;
    readonly description?: string;
    /** Optional format scoping; defaults to the hook's default set. */
    readonly formats?: UseBarcodeScannerOptions["formats"];
    readonly onResult: (result: BarcodeScanResult) => void;
    readonly onClose: () => void;
}

/**
 * Full-screen barcode scanner overlay. Designed to *feel* like a native
 * scanner — live preview fills the viewport, a viewfinder window shows
 * the user where to aim, and a single "Cancel" affordance lets them back
 * out. Everything else is automatic: framing the barcode triggers the
 * decode and closes the sheet.
 *
 * Layout uses fixed positioning over the entire viewport so it works
 * inside any parent (the asset form is itself inside SplitPane / sheets
 * on phone) without leaking layout into the host. Z-index sits above the
 * other dialogs in the app (ConfirmAction = 50, AssetPickerDialog = 50)
 * so a stacked confirmation can't fight us for focus.
 */
export function BarcodeScannerSheet({
    open,
    title = "Scan barcode",
    description = "Point the camera at the barcode. The form will fill automatically.",
    formats,
    onResult,
    onClose,
}: BarcodeScannerSheetProps): ReactElement | null {
    const scanner = useBarcodeScanner({
        ...(formats !== undefined ? { formats } : {}),
        onResult: (result) => {
            // Stop the camera before bubbling the result so the consumer's
            // state-update / re-render doesn't race the next decode tick.
            scanner.stop();
            onResult(result);
        },
    });

    // Start when the sheet opens; stop when it closes or unmounts. The
    // hook itself handles tab-hidden suspend/resume so we don't need to
    // re-implement that here.
    useEffect(() => {
        if (open) {
            void scanner.start();
            return () => {
                scanner.stop();
            };
        }
        return undefined;
        // `scanner.start`/`scanner.stop` are stable from the hook (wrapped
        // in useCallback) but excluding them keeps the effect strictly
        // tied to `open`, which is the actual lifecycle signal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const isCameraPending =
        scanner.status === "starting" || scanner.status === "scanning";
    const showError =
        scanner.status === "permission-denied" ||
        scanner.status === "no-camera" ||
        scanner.status === "unsupported" ||
        scanner.status === "error";

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            style={{
                position: "fixed",
                inset: 0,
                background: "#000",
                zIndex: 60,
                display: "flex",
                flexDirection: "column",
                color: "#fff",
            }}
        >
            {/* Live video. Object-fit: cover keeps the viewfinder pleasant
                on both portrait and landscape; the decoder reads the
                underlying frame at full resolution regardless of CSS
                fit, so cropping in the UI does not hurt detection. */}
            <video
                ref={scanner.videoRef}
                autoPlay
                playsInline
                muted
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                }}
            />

            {isCameraPending ? <ViewfinderOverlay /> : null}

            {/* Top bar: title + close button. Sits over the video, with a
                soft gradient so white text stays readable against any
                background frame. */}
            <div
                style={{
                    position: "relative",
                    padding: `${space[3]} ${space[4]}`,
                    background:
                        "linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))",
                    zIndex: 1,
                }}
            >
                <Stack direction="row" align="center" justify="space-between" gap={2}>
                    <Stack gap={0}>
                        <Text role="heading" style={{ color: "#fff" }}>
                            {title}
                        </Text>
                        <Text role="muted" style={{ color: "rgba(255,255,255,0.75)" }}>
                            {description}
                        </Text>
                    </Stack>
                    <IconButton
                        icon={<X size={18} />}
                        label="Cancel scan"
                        onClick={onClose}
                    />
                </Stack>
            </div>

            <div style={{ flex: 1 }} />

            {/* Bottom: status / error pane. Anchored at the bottom so the
                user's thumb stays in the action zone on phone. */}
            <div
                style={{
                    position: "relative",
                    padding: space[4],
                    background:
                        "linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0))",
                    zIndex: 1,
                }}
            >
                {showError ? (
                    <Stack gap={3}>
                        <Stack direction="row" align="center" gap={2}>
                            <Camera size={16} style={{ color: palette.danger }} />
                            <Text role="body" style={{ color: "#fff" }}>
                                {scanner.message ?? "Scanner error."}
                            </Text>
                        </Stack>
                        <Stack direction="row" gap={2} justify="flex-end">
                            <Button variant="secondary" onClick={onClose}>
                                Close
                            </Button>
                            {scanner.status === "permission-denied" ||
                            scanner.status === "error" ? (
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        void scanner.start();
                                    }}
                                >
                                    Try again
                                </Button>
                            ) : null}
                        </Stack>
                    </Stack>
                ) : (
                    <Stack direction="row" align="center" justify="space-between" gap={2}>
                        <Text role="muted" style={{ color: "rgba(255,255,255,0.85)" }}>
                            {scanner.status === "starting"
                                ? "Starting camera…"
                                : "Aim at the barcode."}
                        </Text>
                    </Stack>
                )}
            </div>
        </div>
    );
}

/**
 * Aim-here overlay. Pure CSS, no canvas — keeps the GPU free for the
 * detector. Four corner brackets are visually unambiguous (more than a
 * full rectangle, less obtrusive than a tinted scrim) and read instantly
 * as "frame the thing inside this".
 */
function ViewfinderOverlay(): ReactElement {
    const cornerSize = 28;
    const corner = {
        position: "absolute",
        width: cornerSize,
        height: cornerSize,
        border: "3px solid #fff",
        boxShadow: "0 0 8px rgba(0,0,0,0.45)",
    } as const;

    return (
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 0,
            }}
        >
            <div
                style={{
                    position: "relative",
                    width: "min(78vw, 360px)",
                    height: "min(48vw, 220px)",
                }}
            >
                {/* Top-left */}
                <div style={{ ...corner, top: 0, left: 0, borderRight: 0, borderBottom: 0 }} />
                {/* Top-right */}
                <div style={{ ...corner, top: 0, right: 0, borderLeft: 0, borderBottom: 0 }} />
                {/* Bottom-left */}
                <div style={{ ...corner, bottom: 0, left: 0, borderRight: 0, borderTop: 0 }} />
                {/* Bottom-right */}
                <div style={{ ...corner, bottom: 0, right: 0, borderLeft: 0, borderTop: 0 }} />
            </div>
        </div>
    );
}
